import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import * as Tooltip from '@radix-ui/react-tooltip';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { AuthProvider, useAuth } from '@/lib/auth/AuthProvider';
import { LoginPage } from '@/features/auth/LoginPage';
import { JitMark } from '@/brand/JitMark';
import { isApiError } from '@/lib/api/errors';
import { router } from './router';

/*
  Provider stack and the authentication gate.

  Order matters: ThemeProvider outermost so even the loading state and the login page are
  themed, then the query client, then auth, then the router. The router is only mounted once
  there is a signed in user, which means no route component ever has to defend against a null
  user and no protected page renders for a frame before redirecting.

  The tooltip provider is here rather than per component, because Radix expects one provider
  and shares the open and close delays across every tooltip in the tree.
*/

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Never retry an authentication or authorization failure. The client has already
        // tried exactly one refresh; retrying a 401 or a 403 just burns requests and, with
        // refresh token rotation, a retry storm looks like token theft to the server.
        if (isApiError(error)) {
          if (
            error.kind === 'unauthenticated' ||
            error.kind === 'forbidden' ||
            error.kind === 'not_found' ||
            error.kind === 'validation'
          ) {
            return false;
          }
        }
        return failureCount < 2;
      },
    },
  },
});

function BootstrapScreen(): React.JSX.Element {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas" role="status">
      <div className="flex flex-col items-center gap-3">
        <JitMark size={40} />
        <p className="text-sm text-fg-muted">Restoring your session</p>
      </div>
    </div>
  );
}

function AuthGate(): React.JSX.Element {
  const { status } = useAuth();

  if (status === 'bootstrapping') {
    return <BootstrapScreen />;
  }
  if (status === 'signed_out') {
    return <LoginPage />;
  }
  return <RouterProvider router={router} />;
}

export function App(): React.JSX.Element {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <Tooltip.Provider delayDuration={300} skipDelayDuration={150}>
          <AuthProvider>
            <AuthGate />
          </AuthProvider>
        </Tooltip.Provider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
